import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";
import "./AdminCommunicationsCenter.css";

type View="COMPOSE"|"CAMPAIGNS"|"JOBS";
type TargetType=
  |"ALL_SCHOOLS"
  |"ROLLOUT_PENDING"
  |"ASSESSMENT_PENDING"
  |"BADGE_UNCLAIMED"
  |"REPORT_ACTION"
  |"PRIVACY_ATTENTION"
  |"SUPPORT_ACTIVE"
  |"CUSTOM";

type PreviewSchool={
  schoolId:string;
  schoolName:string;
  schoolCode?:string|null;
  targetReason:string;
  recipientCount:number;
  missingEmailCount:number;
};

type Preview={
  targetType:TargetType;
  recipientRoles:string[];
  targetCount:number;
  recipientCount:number;
  missingEmailCount:number;
  providerConfigured:boolean;
  schools:PreviewSchool[];
};

type CampaignRow={
  campaignId:string;
  campaignName:string;
  campaignType:TargetType;
  recipientRoles:string[];
  subject:string;
  actionPath?:string|null;
  status:"DRAFT"|"DISPATCHED"|"CANCELLED"|string;
  createdAt:string;
  dispatchedAt?:string|null;
  targetCount:number;
  previewRecipientCount:number;
  jobCount:number;
  pendingJobs:number;
  acceptedJobs:number;
  retryableJobs:number;
  permanentFailures:number;
};

type CampaignTarget={
  schoolId:string;
  schoolName:string;
  schoolCode?:string|null;
  targetReason:string;
  recipientCount:number;
  missingEmailCount:number;
};

type CampaignJob={
  jobId:string;
  schoolId:string;
  schoolName:string;
  recipientMasked?:string|null;
  status:string;
  attemptCount:number;
  provider?:string|null;
  providerRef?:string|null;
  lastErrorCode?:string|null;
  nextAttemptAt?:string|null;
  createdAt:string;
  updatedAt:string;
};

type CampaignDetail={
  campaignId:string;
  campaignName:string;
  campaignType:TargetType;
  recipientRoles:string[];
  subject:string;
  bodyText:string;
  actionPath?:string|null;
  status:string;
  createdAt:string;
  dispatchedAt?:string|null;
  targets:CampaignTarget[];
  jobs:CampaignJob[];
};

type Workspace={
  provider:{provider:string;configured:boolean;workerTokenConfigured:boolean};
  summary:{
    campaignCount:number;
    draftCampaigns:number;
    dispatchedCampaigns:number;
    queuedJobs:number;
    acceptedJobs:number;
    failedJobs:number;
  };
  campaigns:CampaignRow[];
  selectedCampaign?:CampaignDetail|null;
};

type Template={
  label:string;
  description:string;
  subject:string;
  body:string;
  actionPath:string;
};

const TEMPLATES:Record<TargetType,Template>={
  ALL_SCHOOLS:{
    label:"전체 학교 안내",
    description:"Scope 내 모든 활성 학교 담당자",
    subject:"[RS CAREER] 운영 안내를 확인해주세요",
    body:"RS CAREER ERP 운영 안내입니다. 아래 바로가기를 통해 현재 학교 운영상태와 필요한 업무를 확인해주세요.",
    actionPath:"/"
  },
  ROLLOUT_PENDING:{
    label:"학교 개통 미완료",
    description:"초대 Claim·학년도·학생등록이 남은 Rollout 학교",
    subject:"[RS CAREER] 학교 개통 설정을 완료해주세요",
    body:"학교 개통 절차가 아직 완료되지 않았습니다. 담당교사 계정 연결과 학교 초기설정을 확인해주세요.",
    actionPath:"/"
  },
  ASSESSMENT_PENDING:{
    label:"검사 참여 리마인드",
    description:"배정된 검사 중 미완료 학생이 있는 학교",
    subject:"[RS CAREER] 진로검사 미완료 현황을 확인해주세요",
    body:"현재 학교에 완료되지 않은 진로검사가 있습니다. 진로검사 메뉴에서 대상 학생과 진행상태를 확인해주세요.",
    actionPath:"/assessments"
  },
  BADGE_UNCLAIMED:{
    label:"Life Stamp 미수령",
    description:"발급됐지만 학생 Wallet 수령이 남은 학교",
    subject:"[RS CAREER] 학생 Life Stamp 수령 현황을 확인해주세요",
    body:"현재 학교에 아직 수령되지 않은 Life Stamp가 있습니다. 디지털배지 메뉴에서 학생별 발급·전달·수령 상태를 확인해주세요.",
    actionPath:"/badges"
  },
  REPORT_ACTION:{
    label:"보고서 미제출·보완",
    description:"공식보고 미제출 또는 보완요청 학교",
    subject:"[RS CAREER] 학교 공식보고 현황을 확인해주세요",
    body:"학교 공식보고 제출 또는 보완이 필요한 상태입니다. 리포트 메뉴에서 현재 상태를 확인하고 필요한 작업을 진행해주세요.",
    actionPath:"/reports"
  },
  PRIVACY_ATTENTION:{
    label:"개인정보 정비",
    description:"보호자·Purpose·공유 상태에 정비 신호가 있는 학교",
    subject:"[RS CAREER] 보호자 동의·공유 설정을 확인해주세요",
    body:"학교 개인정보·동의·공유 운영에서 확인할 항목이 있습니다. 동의·공유 메뉴에서 보호자 등록과 목적정책, 공유 상태를 점검해주세요.",
    actionPath:"/privacy"
  },
  SUPPORT_ACTIVE:{
    label:"지원 진행 학교",
    description:"지역 모니터링 또는 지원 중인 학교",
    subject:"[RS CAREER] 지역 지원 후속 업무를 확인해주세요",
    body:"현재 학교는 지역 지원 또는 모니터링 대상입니다. 학교 운영 현황과 후속 업무를 확인해주세요.",
    actionPath:"/operations"
  },
  CUSTOM:{
    label:"직접 선택",
    description:"관리자가 직접 선택한 학교",
    subject:"[RS CAREER] 학교 운영 안내",
    body:"RS CAREER ERP 운영 관련 안내입니다. 아래 바로가기를 통해 내용을 확인해주세요.",
    actionPath:"/"
  }
};

const TARGET_ORDER:TargetType[]=[
  "ROLLOUT_PENDING",
  "ASSESSMENT_PENDING",
  "BADGE_UNCLAIMED",
  "REPORT_ACTION",
  "PRIVACY_ATTENTION",
  "SUPPORT_ACTIVE",
  "ALL_SCHOOLS",
  "CUSTOM"
];

function fmt(value?:string|null){
  if(!value)return "-";
  return new Date(value).toLocaleString("ko-KR",{
    year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"
  });
}

function statusLabel(value:string){
  const map:Record<string,string>={
    DRAFT:"Draft",
    DISPATCHED:"발송 큐 생성",
    QUEUED:"큐 대기",
    SENDING:"전송 중",
    ACCEPTED:"전달 완료",
    RETRYABLE_FAILURE:"재시도 필요",
    PERMANENT_FAILURE:"영구 실패"
  };
  return map[value] || value;
}

function navigate(path:string){
  window.history.pushState({},"",path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function AdminCommunicationsCenter({
  client,
  rootOrganizationId
}:{client:PlatformClient;rootOrganizationId:string}){
  const [view,setView]=useState<View>("COMPOSE");
  const [workspace,setWorkspace]=useState<Workspace|null>(null);
  const [allSchools,setAllSchools]=useState<PreviewSchool[]>([]);
  const [preview,setPreview]=useState<Preview|null>(null);
  const [targetType,setTargetType]=useState<TargetType>("BADGE_UNCLAIMED");
  const [roleKeys,setRoleKeys]=useState<string[]>(["CAREER_TEACHER","SCHOOL_ADMIN"]);
  const [customSchoolIds,setCustomSchoolIds]=useState<string[]>([]);
  const [campaignName,setCampaignName]=useState(TEMPLATES.BADGE_UNCLAIMED.label);
  const [subject,setSubject]=useState(TEMPLATES.BADGE_UNCLAIMED.subject);
  const [body,setBody]=useState(TEMPLATES.BADGE_UNCLAIMED.body);
  const [actionPath,setActionPath]=useState(TEMPLATES.BADGE_UNCLAIMED.actionPath);
  const [selectedCampaignId,setSelectedCampaignId]=useState("");
  const [campaignQuery,setCampaignQuery]=useState("");
  const [campaignStatus,setCampaignStatus]=useState("ALL");
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");
  const [message,setMessage]=useState("");
  const [refresh,setRefresh]=useState(0);

  useEffect(()=>{
    let active=true;
    client.rpc("admin_communications_workspace",{
      p_root_organization_id:rootOrganizationId,
      p_campaign_id:selectedCampaignId || null
    }).then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      const next=data as Workspace;
      setWorkspace(next);
      if(!selectedCampaignId && next.campaigns[0]){
        setSelectedCampaignId(next.campaigns[0].campaignId);
      }
    });
    return()=>{active=false;};
  },[client,rootOrganizationId,selectedCampaignId,refresh]);

  useEffect(()=>{
    let active=true;
    client.rpc("admin_communication_target_preview",{
      p_root_organization_id:rootOrganizationId,
      p_target_type:"ALL_SCHOOLS",
      p_school_ids:null,
      p_role_keys:roleKeys
    }).then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      setAllSchools((data as Preview).schools);
    });
    return()=>{active=false;};
  },[client,rootOrganizationId,roleKeys.join("|")]);

  useEffect(()=>{
    let active=true;
    setError("");
    client.rpc("admin_communication_target_preview",{
      p_root_organization_id:rootOrganizationId,
      p_target_type:targetType,
      p_school_ids:targetType==="CUSTOM"?customSchoolIds:null,
      p_role_keys:roleKeys
    }).then(({data,error})=>{
      if(!active)return;
      if(error){setPreview(null);setError(error.message);return;}
      setPreview(data as Preview);
    });
    return()=>{active=false;};
  },[client,rootOrganizationId,targetType,roleKeys.join("|"),customSchoolIds.join("|")]);

  const selectedCampaign=workspace?.selectedCampaign || null;

  const visibleCampaigns=useMemo(()=>{
    const keyword=campaignQuery.trim().toLowerCase();
    return (workspace?.campaigns || []).filter((item)=>{
      if(campaignStatus!=="ALL" && item.status!==campaignStatus)return false;
      return !keyword || [item.campaignName,item.subject,item.campaignType]
        .some((value)=>value.toLowerCase().includes(keyword));
    });
  },[workspace,campaignQuery,campaignStatus]);

  function applyTemplate(type:TargetType){
    const template=TEMPLATES[type];
    setTargetType(type);
    setCampaignName(template.label);
    setSubject(template.subject);
    setBody(template.body);
    setActionPath(template.actionPath);
    if(type!=="CUSTOM")setCustomSchoolIds([]);
  }

  function toggleRole(role:string){
    setRoleKeys((current)=>
      current.includes(role)
        ? current.length===1?current:current.filter((item)=>item!==role)
        : [...current,role]
    );
  }

  function toggleSchool(id:string){
    setCustomSchoolIds((current)=>
      current.includes(id)?current.filter((item)=>item!==id):[...current,id]
    );
  }

  async function createCampaign(){
    if(!preview || !campaignName.trim() || !subject.trim() || !body.trim())return;
    setBusy("create");
    setError("");
    setMessage("");

    const {data,error}=await client.rpc("admin_create_communication_campaign",{
      p_root_organization_id:rootOrganizationId,
      p_campaign_name:campaignName.trim(),
      p_campaign_type:targetType,
      p_school_ids:targetType==="CUSTOM"?customSchoolIds:null,
      p_role_keys:roleKeys,
      p_subject:subject.trim(),
      p_body_text:body.trim(),
      p_action_path:actionPath.trim() || null
    });

    setBusy("");
    if(error){setError(error.message);return;}

    const result=data as {campaignId:string;targetCount:number;recipientCount:number};
    setSelectedCampaignId(result.campaignId);
    setView("CAMPAIGNS");
    setMessage(`Draft Campaign 저장 완료 · 학교 ${result.targetCount} · 예상 수신자 ${result.recipientCount}`);
    setRefresh((value)=>value+1);
  }

  async function dispatchCampaign(){
    if(!selectedCampaign)return;
    setBusy("dispatch");
    setError("");
    setMessage("");

    const {data,error}=await client.rpc("admin_dispatch_communication_campaign",{
      p_root_organization_id:rootOrganizationId,
      p_campaign_id:selectedCampaign.campaignId
    });

    setBusy("");
    if(error){setError(error.message);return;}

    const result=data as {
      jobCount:number;
      queuedCount:number;
      missingEmailCount:number;
      providerReady:boolean;
      workerTriggered:boolean;
    };

    setMessage(
      result.providerReady
        ? `발송 큐 ${result.jobCount}건 생성 · Worker ${result.workerTriggered?"호출":"대기"}`
        : `발송 큐 ${result.jobCount}건 생성 · Email Provider 미구성으로 Worker는 호출하지 않았습니다.`
    );
    setRefresh((value)=>value+1);
    setView("JOBS");
  }

  async function retryCampaign(){
    if(!selectedCampaign)return;
    setBusy("retry");
    setError("");
    setMessage("");

    const {data,error}=await client.rpc("admin_retry_communication_campaign",{
      p_root_organization_id:rootOrganizationId,
      p_campaign_id:selectedCampaign.campaignId
    });

    setBusy("");
    if(error){setError(error.message);return;}

    const result=data as {retryableReset:number;providerReady:boolean;workerTriggered:boolean};
    setMessage(
      result.providerReady
        ? `재시도 대상 ${result.retryableReset}건 정리 · Worker ${result.workerTriggered?"호출":"대기"}`
        : "Email Provider가 아직 구성되지 않아 Worker를 호출하지 않았습니다."
    );
    setRefresh((value)=>value+1);
  }

  if(!workspace)return <p className="muted">커뮤니케이션 운영현황을 불러오는 중…</p>;

  const s=workspace.summary;

  return (
    <section className="communications-page">
      <header className="communications-hero">
        <div>
          <p className="eyebrow">COMMUNICATIONS & NOTIFICATION OPERATIONS CENTER</p>
          <h2>학교 커뮤니케이션·알림 운영센터</h2>
          <p>
            학교 운영상태를 기준으로 대상을 먼저 미리보고, Draft Campaign을 저장한 뒤
            발송 큐를 명시적으로 생성합니다. 수신자 주소는 운영 이력에서 마스킹합니다.
          </p>
        </div>
        <div className={"communications-provider "+(workspace.provider.configured?"ready":"blocked")}>
          <span>EMAIL PROVIDER</span>
          <strong>{workspace.provider.configured?"Resend Ready":"Provider 미구성"}</strong>
          <small>Worker Token {workspace.provider.workerTokenConfigured?"✓":"✕"}</small>
        </div>
      </header>

      {error && <p className="error">{error}</p>}
      {message && <p className="teacher-success">{message}</p>}

      {!workspace.provider.configured && (
        <div className="communications-warning">
          <div>
            <strong>현재 DEV Email Provider가 미구성입니다.</strong>
            <span>Campaign과 발송 Job은 만들 수 있지만 Resend API key / From address가 준비될 때까지 Worker 자동호출은 차단됩니다.</span>
          </div>
          <button className="secondary" onClick={()=>navigate("/platform-configuration")}>플랫폼 설정</button>
        </div>
      )}

      <div className="communications-kpis">
        <CommKpi label="Campaign" value={s.campaignCount} />
        <CommKpi label="Draft" value={s.draftCampaigns} />
        <CommKpi label="발송 큐 생성" value={s.dispatchedCampaigns} />
        <CommKpi label="Queue/Retry" value={s.queuedJobs} tone={s.queuedJobs>0?"warn":"normal"} />
        <CommKpi label="전달 완료" value={s.acceptedJobs} />
        <CommKpi label="실패" value={s.failedJobs} tone={s.failedJobs>0?"danger":"normal"} />
      </div>

      <nav className="communications-tabs">
        <button className={view==="COMPOSE"?"active":""} onClick={()=>setView("COMPOSE")}>Campaign 작성</button>
        <button className={view==="CAMPAIGNS"?"active":""} onClick={()=>setView("CAMPAIGNS")}>Campaign 관리</button>
        <button className={view==="JOBS"?"active":""} onClick={()=>setView("JOBS")}>발송 Job</button>
      </nav>

      {view==="COMPOSE" && (
        <div className="communications-compose-layout">
          <section className="communications-card">
            <div className="communications-section-head">
              <div><p className="eyebrow">TARGET RULE</p><h3>대상 학교 선택</h3></div>
              <span>상태기반 자동선별</span>
            </div>

            <div className="target-rule-grid">
              {TARGET_ORDER.map((type)=>(
                <button key={type} className={targetType===type?"active":""} onClick={()=>applyTemplate(type)}>
                  <strong>{TEMPLATES[type].label}</strong>
                  <span>{TEMPLATES[type].description}</span>
                </button>
              ))}
            </div>

            <div className="recipient-role-box">
              <div><strong>수신 역할</strong><span>같은 사람이 두 역할을 가져도 이메일은 1회만 생성합니다.</span></div>
              <label><input type="checkbox" checked={roleKeys.includes("CAREER_TEACHER")} onChange={()=>toggleRole("CAREER_TEACHER")} />진로교사</label>
              <label><input type="checkbox" checked={roleKeys.includes("SCHOOL_ADMIN")} onChange={()=>toggleRole("SCHOOL_ADMIN")} />학교관리자</label>
            </div>

            {targetType==="CUSTOM" && (
              <div className="custom-school-picker">
                <div className="communications-section-head compact">
                  <div><strong>직접 선택</strong><small>{customSchoolIds.length}/{allSchools.length}개교</small></div>
                  <button className="text-button" onClick={()=>setCustomSchoolIds(allSchools.map((item)=>item.schoolId))}>전체 선택</button>
                </div>
                <div>
                  {allSchools.map((school)=>(
                    <label key={school.schoolId}>
                      <input type="checkbox" checked={customSchoolIds.includes(school.schoolId)} onChange={()=>toggleSchool(school.schoolId)} />
                      <span><strong>{school.schoolName}</strong><small>{school.schoolCode || "-"}</small></span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </section>

          <section className="communications-card preview-card">
            <div className="communications-section-head">
              <div><p className="eyebrow">TARGET PREVIEW</p><h3>발송 전 미리보기</h3></div>
              <span>{preview?.targetCount || 0}개교</span>
            </div>

            <div className="preview-summary">
              <div><span>대상 학교</span><strong>{preview?.targetCount || 0}</strong></div>
              <div><span>예상 수신자</span><strong>{preview?.recipientCount || 0}</strong></div>
              <div><span>이메일 누락</span><strong>{preview?.missingEmailCount || 0}</strong></div>
            </div>

            <div className="preview-school-list">
              {(preview?.schools || []).map((school)=>(
                <article key={school.schoolId}>
                  <div><strong>{school.schoolName}</strong><small>{school.schoolCode || "-"}</small></div>
                  <p>{school.targetReason}</p>
                  <span>수신 {school.recipientCount}{school.missingEmailCount>0?" · 누락 "+school.missingEmailCount:""}</span>
                </article>
              ))}
              {preview && !preview.schools.length && <p className="case-empty">현재 조건에 맞는 학교가 없습니다.</p>}
            </div>
          </section>

          <section className="communications-card campaign-editor">
            <div className="communications-section-head">
              <div><p className="eyebrow">MESSAGE</p><h3>Campaign 문구</h3></div>
              <span>Draft 저장 후 별도 Dispatch</span>
            </div>

            <div className="campaign-editor-grid">
              <label>Campaign 이름
                <input value={campaignName} onChange={(event)=>setCampaignName(event.target.value)} />
              </label>
              <label>Center 바로가기
                <input value={actionPath} onChange={(event)=>setActionPath(event.target.value)} placeholder="/operations" />
              </label>
              <label className="wide">메일 제목
                <input value={subject} onChange={(event)=>setSubject(event.target.value)} />
              </label>
              <label className="wide">본문
                <textarea rows={7} value={body} onChange={(event)=>setBody(event.target.value)} />
              </label>
            </div>

            <div className="campaign-editor-footer">
              <p>여기서는 Campaign만 저장합니다. 이메일 Job은 Campaign 상세에서 <b>발송 큐 생성</b>을 눌러야 만들어집니다.</p>
              <button
                className="primary"
                disabled={busy!=="" || !preview || preview.targetCount===0 || !campaignName.trim() || !subject.trim() || !body.trim()}
                onClick={createCampaign}
              >
                {busy==="create"?"저장 중…":"Draft Campaign 저장"}
              </button>
            </div>
          </section>
        </div>
      )}

      {view==="CAMPAIGNS" && (
        <div className="campaign-management-layout">
          <aside className="campaign-list">
            <div className="campaign-tools">
              <input value={campaignQuery} onChange={(event)=>setCampaignQuery(event.target.value)} placeholder="Campaign 검색" />
              <select value={campaignStatus} onChange={(event)=>setCampaignStatus(event.target.value)}>
                <option value="ALL">전체 상태</option>
                <option value="DRAFT">Draft</option>
                <option value="DISPATCHED">발송 큐 생성</option>
              </select>
            </div>
            <div className="panel-title"><strong>Campaign</strong><span>{visibleCampaigns.length}건</span></div>
            {visibleCampaigns.map((campaign)=>(
              <button key={campaign.campaignId} className={selectedCampaignId===campaign.campaignId?"active":""} onClick={()=>setSelectedCampaignId(campaign.campaignId)}>
                <div>
                  <span className={"campaign-state "+campaign.status.toLowerCase()}>{statusLabel(campaign.status)}</span>
                  <span>{campaign.targetCount}개교</span>
                </div>
                <strong>{campaign.campaignName}</strong>
                <p>{campaign.subject}</p>
                <small>{fmt(campaign.createdAt)} · 예상 {campaign.previewRecipientCount}명</small>
              </button>
            ))}
            {!visibleCampaigns.length && <p className="case-empty">Campaign이 없습니다.</p>}
          </aside>

          <main className="campaign-detail">
            {selectedCampaign ? (
              <>
                <header>
                  <div>
                    <div className="campaign-detail-tags">
                      <span className={"campaign-state "+selectedCampaign.status.toLowerCase()}>{statusLabel(selectedCampaign.status)}</span>
                      <span>{TEMPLATES[selectedCampaign.campaignType]?.label || selectedCampaign.campaignType}</span>
                    </div>
                    <h2>{selectedCampaign.campaignName}</h2>
                    <p>{selectedCampaign.subject}</p>
                  </div>
                  {selectedCampaign.status==="DRAFT" && (
                    <button className="primary" disabled={busy!==""} onClick={dispatchCampaign}>
                      {busy==="dispatch"?"Queue 생성 중…":"발송 큐 생성"}
                    </button>
                  )}
                </header>

                <section className="communications-card campaign-message-card">
                  <div className="communications-section-head"><div><p className="eyebrow">MESSAGE SNAPSHOT</p><h3>발송 문구</h3></div></div>
                  <strong>{selectedCampaign.subject}</strong>
                  <p>{selectedCampaign.bodyText}</p>
                  <small>Center action: {selectedCampaign.actionPath || "-"}</small>
                </section>

                <section className="communications-card">
                  <div className="communications-section-head"><div><p className="eyebrow">TARGET SNAPSHOT</p><h3>대상 학교</h3></div><span>{selectedCampaign.targets.length}개교</span></div>
                  <div className="campaign-target-table">
                    <div className="campaign-target-row head"><span>학교</span><span>선정 사유</span><span>예상 수신</span><span>누락</span></div>
                    {selectedCampaign.targets.map((target)=>(
                      <div className="campaign-target-row" key={target.schoolId}>
                        <span><strong>{target.schoolName}</strong><small>{target.schoolCode || "-"}</small></span>
                        <span>{target.targetReason}</span>
                        <span>{target.recipientCount}</span>
                        <span>{target.missingEmailCount}</span>
                      </div>
                    ))}
                  </div>
                </section>

                {selectedCampaign.status==="DRAFT" && (
                  <div className="dispatch-confirmation">
                    <strong>발송 큐 생성 전 확인</strong>
                    <p>이 작업부터 실제 External Notification Job이 생성됩니다. 현재 Provider가 미구성이면 Job은 Queue에 남고 자동 Worker 호출은 하지 않습니다.</p>
                  </div>
                )}
              </>
            ):<p className="case-empty">Campaign을 선택하세요.</p>}
          </main>
        </div>
      )}

      {view==="JOBS" && (
        <section className="communications-card">
          <div className="communications-section-head">
            <div>
              <p className="eyebrow">DELIVERY JOBS</p>
              <h3>{selectedCampaign?.campaignName || "발송 Job"}</h3>
            </div>
            <div className="job-actions">
              <button className="secondary" onClick={()=>navigate("/system-operations")}>System Operations</button>
              {selectedCampaign?.status==="DISPATCHED" && (
                <button className="primary" disabled={busy!=="" || !workspace.provider.configured} onClick={retryCampaign}>
                  {busy==="retry"?"재시도 중…":"Retry Worker"}
                </button>
              )}
            </div>
          </div>

          {!workspace.provider.configured && (
            <p className="job-provider-note">Email Provider 미구성 상태라 Retry Worker 버튼을 비활성화했습니다. Job 자체는 Queue에 보존됩니다.</p>
          )}

          <div className="delivery-job-summary">
            <div><span>전체</span><strong>{selectedCampaign?.jobs.length || 0}</strong></div>
            <div><span>Queue/Retry</span><strong>{selectedCampaign?.jobs.filter((item)=>["QUEUED","SENDING","RETRYABLE_FAILURE"].includes(item.status)).length || 0}</strong></div>
            <div><span>Accepted</span><strong>{selectedCampaign?.jobs.filter((item)=>item.status==="ACCEPTED").length || 0}</strong></div>
            <div><span>Failure</span><strong>{selectedCampaign?.jobs.filter((item)=>["RETRYABLE_FAILURE","PERMANENT_FAILURE"].includes(item.status)).length || 0}</strong></div>
          </div>

          <div className="delivery-job-table">
            <div className="delivery-job-row head"><span>학교</span><span>수신자</span><span>상태</span><span>시도</span><span>Provider</span><span>오류</span><span>최근 갱신</span></div>
            {(selectedCampaign?.jobs || []).map((job)=>(
              <div className="delivery-job-row" key={job.jobId}>
                <span>{job.schoolName}</span>
                <span>{job.recipientMasked || "이메일 없음"}</span>
                <span className={"job-state "+job.status.toLowerCase()}>{statusLabel(job.status)}</span>
                <span>{job.attemptCount}</span>
                <span>{job.provider || "-"}</span>
                <span>{job.lastErrorCode || "-"}</span>
                <span>{fmt(job.updatedAt)}</span>
              </div>
            ))}
            {selectedCampaign && !selectedCampaign.jobs.length && <p className="case-empty">아직 발송 Job이 없습니다. Draft Campaign에서 발송 큐를 생성하세요.</p>}
            {!selectedCampaign && <p className="case-empty">Campaign 관리에서 Campaign을 선택하세요.</p>}
          </div>
        </section>
      )}
    </section>
  );
}

function CommKpi({
  label,value,tone="normal"
}:{label:string;value:number;tone?:"normal"|"warn"|"danger"}){
  return <div className={"communications-kpi "+tone}><span>{label}</span><strong>{value.toLocaleString("ko-KR")}</strong></div>;
}
